/**
 * Hodnoty pohybu pro Angular animace.
 *
 * Musí zůstat shodné s `:root` v `src/styles/_motion.scss` — `animate()` neumí číst
 * CSS proměnnou, takže tenhle soubor je jejím zrcadlem. Křivka je `@ease-out`
 * z `theme.less`, tedy tatáž, kterou používají drawer, modal i collapse od ng-zorro.
 */
export const MOTION_PAGE_DURATION = '180ms';

export const MOTION_PAGE_SHIFT = '16px';

export const MOTION_EASE_OUT = 'cubic-bezier(0.215, 0.61, 0.355, 1)';

/**
 * Výchozí odmlka mezi příjezdem postavy a promluvením, zrcadlo `--bp-motion-scene-speak`.
 *
 * ⚠ Čte ji **skladatel i renderer** — autor si ji u bloku může přepsat, a formulář
 * musí nabízet totéž číslo, které student dostane, když ji nepřepíše. Do EL-2262
 * byla dvakrát jako literál a stačilo změnit token, aby se rozešly.
 */
export const MOTION_SCENE_SPEAK_MS = 320;

/**
 * Stropy autorových prodlev scény (A6, revize kurzu 27) — zrcadla
 * `--bp-motion-scene-entry-max` a `--bp-motion-scene-speak-max`.
 *
 * Formulář scény v administraci pouští nejvýš tyto hodnoty; starší bloky s delší
 * prodlevou student dostane oříznuté na strop. Kurz 27 měl u 70 scén 2500 + 1400 ms a bublina byla čitelná až ~6 s po
 * dorolování. Se stropy: příjezd ≤ 600 ms + 620 ms jízda, odmlka ≤ 900 ms + tři
 * kolečka po 110 ms, otevření bubliny ~430 ms → celá scéna ~2,3 s od dorolování,
 * bublina stojí ~1 s po dojezdu postavy.
 */
export const MOTION_SCENE_ENTRY_MAX_MS = 600;

export const MOTION_SCENE_SPEAK_MAX_MS = 900;

/**
 * Body k zapamatování (EL-2269) — zrcadla `--bp-motion-takeaway-*`. Prodleva a krok
 * se odpočítávají v TypeScriptu (pořadí bodu × krok), hatch a chvění běží v CSS.
 * Při `prefers-reduced-motion` se v CSS nulují; TypeScript se ptá `matchMedia` sám.
 */
export const MOTION_TAKEAWAY_DELAY_MS = 2000;

export const MOTION_TAKEAWAY_STEP_MS = 1000;

export const MOTION_TAKEAWAY_HATCH_MS = 450;

export const MOTION_TAKEAWAY_SHAKE_MS = 260;

/**
 * Najetí grafu po dorolování (EL-2285) — zrcadla `--bp-motion-chart*`. Echarts animuje
 * canvas sám a CSS proměnnou nepřečte, proto žijí čísla i tady. Růst jednoho sloupce (1,8 s),
 * krok mezi sloupci a strop pro celý odstup: dohromady nejvýš 2 s, jak chtěl zadavatel.
 */
export const MOTION_CHART_MS = 1800;

export const MOTION_CHART_STEP_MS = 90;

export const MOTION_CHART_STAGGER_MAX_MS = 200;

/** Minimální tvar routy, který výpočet hloubky potřebuje — kvůli testovatelnosti. */
export interface MotionRouteLike {
  data: { [key: string]: unknown };
  firstChild: MotionRouteLike | null;
}

/**
 * Hloubka průchodu pro právě zobrazenou stránku.
 *
 * Bere POSLEDNÍ hodnotu po cestě od kořene, ne tu z nejhlubší routy. Detail kurzu se
 * hned překlopí na `.../article/:id` a to dítě vlastní `motionDepth` nemá — Angular
 * data do routy s vlastní cestou nedědí (`paramsInheritanceStrategy: 'emptyOnly'`),
 * takže čtení z nejhlubší routy vracelo nulu stejně jako na rozcestníku a přechod
 * do kurzu se vůbec neanimoval.
 */
export function motionDepthOf(root: MotionRouteLike | null): number {
  let route = root;
  let depth = 0;

  while (route) {
    const value = route.data['motionDepth'] as number | undefined;

    if (value !== undefined) {
      depth = value;
    }

    route = route.firstChild;
  }

  return depth;
}

/**
 * Má uživatel v systému zapnuté omezení pohybu? (EL-2296)
 *
 * CSS to řeší samo (`_motion.scss`), ale co počítá TypeScript — časovače bodů
 * k zapamatování, animace grafu v canvasu, `BrowserAnimationsModule` — se musí zeptat.
 * Do EL-2296 to byly čtyři ruční `matchMedia` a jeden z nich bez kontroly, že
 * `matchMedia` existuje (starší prostředí testů ho nemá).
 */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
